<template>
  <div class="emission-step">
    <h2 class="step-title">Cumplimiento Artículo 492</h2>
    <p class="step-subtitle">
      Antes de emitir, confirma la declaración y, si lo deseas, carga documentos soporte.
    </p>

    <div class="form-section compliance-section">
      <div class="section-header">
        <el-icon class="section-icon"><Warning /></el-icon>
        <span class="section-title">DECLARACION OBLIGATORIA</span>
      </div>

      <div class="compliance-checkbox-panel" :class="{ 'has-error': showError && !accepted }">
        <el-checkbox v-model="accepted" class="privacy-checkbox">
          Declaro que recabé la información requerida por el Artículo 492 de la Ley de Instituciones
          de Seguros y de Fianzas para esta emisión.
        </el-checkbox>
        <p class="field-hint compliance-helper">
          Si no cargas documentos al momento de la emisión, deberán enviarse a
          <a href="mailto:contacto.autos@grupokc.com.mx">contacto.autos@grupokc.com.mx</a>
          dentro de los 7 días posteriores.
        </p>
        <span v-if="showError && !accepted" class="error-text">
          Debes aceptar la declaración del Artículo 492 para continuar.
        </span>
      </div>
    </div>

    <div v-if="accepted" class="form-section">
      <div class="section-header">
        <el-icon class="section-icon"><UploadFilled /></el-icon>
        <span class="section-title">DOCUMENTACION (OPCIONAL)</span>
      </div>

      <div class="documents-grid">
        <div v-for="documentType in complianceDocumentTypes" :key="documentType.key" class="document-upload-card">
          <div class="document-upload-header">
            <strong>{{ documentType.label }}</strong>
            <span class="document-badge">Opcional</span>
          </div>
          <p class="field-hint">{{ documentType.description }}</p>

          <el-upload
            drag
            action="#"
            :auto-upload="false"
            :limit="1"
            :accept="allowedFileExtensions"
            :file-list="documentFiles[documentType.key]"
            :before-upload="(rawFile: File) => beforeDocumentUpload(rawFile, documentType.key)"
            :on-remove="(_file: UploadUserFile, fileList: UploadUserFile[]) => handleDocumentRemove(documentType.key, fileList)"
            :on-exceed="() => handleDocumentExceed(documentType.label)"
            class="document-uploader"
          >
            <el-icon class="upload-icon"><UploadFilled /></el-icon>
            <div class="el-upload__text">
              Arrastra el archivo o <em>haz clic para seleccionarlo</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">{{ getDocumentSelectionLabel(documentType.key) }}</div>
            </template>
          </el-upload>
        </div>
      </div>
    </div>

    <div class="step-actions">
      <el-button type="default" size="large" @click="emit('back')" class="back-button">
        ← Atrás
      </el-button>
      <div class="action-buttons">
        <el-button
          type="success"
          size="large"
          @click="emit('download-offer')"
          :loading="isDownloadingOffer"
          :disabled="isDownloadingOffer || isSendingOffer || isEmitting"
          class="action-button"
        >
          DESCARGAR OFERTA
        </el-button>
        <el-button
          v-if="SHOW_EMAIL_SHARE"
          type="success"
          size="large"
          @click="emit('send-offer')"
          :loading="isSendingOffer"
          :disabled="isDownloadingOffer || isSendingOffer || isEmitting"
          class="action-button"
        >
          ENVIAR OFERTA
        </el-button>
        <el-button
          type="primary"
          size="large"
          :loading="isEmitting"
          :disabled="!canEmit || isEmitting"
          class="action-button emit-button"
          @click="emit('emit-policy')"
        >
          EMITIR POLIZA
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { UploadFilled, Warning } from '@element-plus/icons-vue';
import { ElMessage, type UploadRawFile, type UploadUserFile } from 'element-plus';
import { SHOW_EMAIL_SHARE } from '@/config/features';

type ComplianceDocumentKey = 'identificacion' | 'domicilio' | 'rfcCurp' | 'adicional';

interface ComplianceDocumentType {
  key: ComplianceDocumentKey;
  label: string;
  description: string;
}

interface ComplianceDocumentMeta {
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
}

interface Props {
  modelValue: boolean;
  showError: boolean;
  canEmit: boolean;
  isEmitting: boolean;
  isDownloadingOffer: boolean;
  isSendingOffer: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:documentCount', value: number): void;
  (e: 'update:documentsMetadata', value: string): void;
  (e: 'update:acceptedAt', value: string): void;
  (e: 'back'): void;
  (e: 'download-offer'): void;
  (e: 'send-offer'): void;
  (e: 'emit-policy'): void;
}>();

const complianceDocumentTypes: ComplianceDocumentType[] = [
  {
    key: 'identificacion',
    label: 'Identificacion oficial',
    description: 'INE, pasaporte u otra identificacion vigente.'
  },
  {
    key: 'domicilio',
    label: 'Comprobante de domicilio',
    description: 'Recibo o comprobante reciente del contratante.'
  },
  {
    key: 'rfcCurp',
    label: 'RFC / CURP',
    description: 'Documento fiscal o CURP, cuando aplique.'
  },
  {
    key: 'adicional',
    label: 'Documento adicional',
    description: 'Cualquier soporte complementario del expediente.'
  }
];

const accepted = computed({
  get: () => props.modelValue,
  set: (val: boolean) => {
    emit('update:modelValue', val);
    if (val && !complianceAcceptedAt.value) {
      complianceAcceptedAt.value = new Date().toISOString();
      emit('update:acceptedAt', complianceAcceptedAt.value);
    }
  }
});

const complianceAcceptedAt = ref('');
const documentFiles = ref<Record<ComplianceDocumentKey, UploadUserFile[]>>({
  identificacion: [],
  domicilio: [],
  rfcCurp: [],
  adicional: []
});

const allowedFileExtensions = '.pdf,.jpg,.jpeg,.png';
const maxFileSizeInBytes = 10 * 1024 * 1024;

const mapUploadFileToMeta = (file: UploadUserFile): ComplianceDocumentMeta => ({
  name: file.name,
  size: file.size ?? 0,
  type: file.raw?.type ?? '',
  uploadedAt: new Date().toISOString()
});

const complianceDocumentsMetadata = computed<Record<ComplianceDocumentKey, ComplianceDocumentMeta[]>>(() => ({
  identificacion: documentFiles.value.identificacion.map(mapUploadFileToMeta),
  domicilio: documentFiles.value.domicilio.map(mapUploadFileToMeta),
  rfcCurp: documentFiles.value.rfcCurp.map(mapUploadFileToMeta),
  adicional: documentFiles.value.adicional.map(mapUploadFileToMeta)
}));

const selectedDocumentsCount = computed(() =>
  Object.values(documentFiles.value).reduce((total, files) => total + files.length, 0)
);

watch(selectedDocumentsCount, (newVal) => {
  emit('update:documentCount', newVal);
});

watch(
  complianceDocumentsMetadata,
  (newVal) => {
    emit('update:documentsMetadata', JSON.stringify(newVal));
  },
  { deep: true }
);

const getDocumentSelectionLabel = (key: ComplianceDocumentKey): string => {
  const files = documentFiles.value[key];
  if (!files.length) return 'Sin archivo cargado.';
  return files.map((file) => file.name).join(', ');
};

const beforeDocumentUpload = (rawFile: File, key: ComplianceDocumentKey): boolean => {
  const isValidMime = ['application/pdf', 'image/jpeg', 'image/png'].includes(rawFile.type);
  const hasValidExtension = /\.(pdf|jpe?g|png)$/i.test(rawFile.name);

  if (!isValidMime && !hasValidExtension) {
    ElMessage.error('Solo se permiten archivos PDF, JPG o PNG');
    return false;
  }

  if (rawFile.size > maxFileSizeInBytes) {
    ElMessage.error('Cada archivo debe pesar maximo 10 MB');
    return false;
  }

  if (!complianceAcceptedAt.value) {
    complianceAcceptedAt.value = new Date().toISOString();
    emit('update:acceptedAt', complianceAcceptedAt.value);
  }

  const uploadRawFile = rawFile as UploadRawFile;
  uploadRawFile.uid = uploadRawFile.uid || Date.now();
  documentFiles.value[key] = [{
    name: rawFile.name,
    size: rawFile.size,
    status: 'success',
    percentage: 100,
    raw: uploadRawFile
  }];

  return false;
};

const handleDocumentRemove = (key: ComplianceDocumentKey, fileList: UploadUserFile[]) => {
  documentFiles.value[key] = [...fileList];
};

const handleDocumentExceed = (documentLabel: string) => {
  ElMessage.warning(`Solo puedes cargar un archivo en ${documentLabel}`);
};
</script>

<style scoped src="./compliance492.css"></style>
